// Client-side subset of the settings schema, for inline errors as the admin types. The server
// (and the Go validator) stays authoritative; this only mirrors the keywords the descriptors carry.
import { SETTINGS_FIELDS_DOC } from "./fields.gen";
import { getPath, isObject, type Json } from "./inherit";

export interface FieldDescriptor {
  path: string;
  section: string;
  owner: string;
  type: string[];
  label: string;
  help: string;
  widget: string | null;
  default: Json;
  required: boolean;
  description: string;
  apply: string;
  customer_editable: boolean;
  customer_readonly: boolean;
  staff_only: boolean;
  secret_ref: boolean;
  server_generated: boolean;
  deprecated: boolean;
  scope: string;
  iac: string | null;
  env: string | null;
  enum: Json[] | null;
  pattern: string | null;
  minimum: number | null;
  maximum: number | null;
  min_length: number | null;
  max_length: number | null;
  min_items: number | null;
  max_items: number | null;
  unique_items: boolean;
  items: { type?: string | string[]; pattern?: string; minLength?: number; maxLength?: number; enum?: Json[] } | null;
  children: string[];
}

export interface SectionDescriptor {
  name: string;
  owner: string;
  label: string;
  required: boolean;
}

export const SECTIONS = SETTINGS_FIELDS_DOC.sections as SectionDescriptor[];
export const FIELDS = SETTINGS_FIELDS_DOC.fields as unknown as FieldDescriptor[];

/** Field path → message, dotted like the API's error paths. */
export type SettingsErrors = Record<string, string>;

/** The leaf and list fields of a section (objects only group), in schema order. */
export function sectionFields(section: string, fields: readonly FieldDescriptor[] = FIELDS): FieldDescriptor[] {
  return fields.filter((f) => f.section === section && !isGroup(f));
}

export function isGroup(field: FieldDescriptor): boolean {
  return field.type.includes("object") && field.children.length > 0;
}

function typeOf(value: Json): string {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  if (typeof value === "number") return Number.isInteger(value) ? "integer" : "number";
  return typeof value;
}

function typeMatches(types: string[], value: Json): boolean {
  const actual = typeOf(value);
  return types.includes(actual) || (actual === "integer" && types.includes("number"));
}

function matches(pattern: string, text: string): boolean {
  try {
    return new RegExp(pattern).test(text);
  } catch {
    return true; // a pattern JavaScript cannot read is the server's to judge
  }
}

function checkScalar(
  value: Json,
  rule: { pattern?: string | null | undefined; min_length?: number | null | undefined; max_length?: number | null | undefined; minimum?: number | null | undefined; maximum?: number | null | undefined; enum?: Json[] | null | undefined },
): string | null {
  if (rule.enum && !rule.enum.includes(value)) return `Must be one of: ${rule.enum.join(", ")}.`;
  if (typeof value === "string") {
    if (rule.min_length != null && value.length < rule.min_length) return `Must be at least ${rule.min_length} characters.`;
    if (rule.max_length != null && value.length > rule.max_length) return `Must be at most ${rule.max_length} characters.`;
    if (rule.pattern && !matches(rule.pattern, value)) return "Not in the expected format.";
  }
  if (typeof value === "number") {
    if (rule.minimum != null && value < rule.minimum) return `Must be at least ${rule.minimum}.`;
    if (rule.maximum != null && value > rule.maximum) return `Must be at most ${rule.maximum}.`;
  }
  return null;
}

/** Why one entry of a list field breaks the schema's item rule, or null. */
export function itemProblem(field: FieldDescriptor, entry: Json): string | null {
  const item = field.items;
  if (!item) return null;
  return checkScalar(entry, { pattern: item.pattern, min_length: item.minLength, max_length: item.maxLength, enum: item.enum });
}

/** A list of strings, edited one entry per row (the schema's items are plain strings). */
export function isStringList(field: FieldDescriptor): boolean {
  const items = field.items?.type;
  return field.type.length === 1 && field.type[0] === "array" && field.widget === null && (items === "string" || (Array.isArray(items) && items.length === 1 && items[0] === "string")) && !field.items?.enum;
}

function checkField(field: FieldDescriptor, value: Json): string | null {
  if (!typeMatches(field.type, value)) return `Must be ${field.type.join(" or ")}.`;
  if (Array.isArray(value)) {
    if (field.min_items != null && value.length < field.min_items) return `Add at least ${field.min_items}.`;
    if (field.max_items != null && value.length > field.max_items) return `At most ${field.max_items} allowed.`;
    if (field.unique_items && new Set(value.map((v) => JSON.stringify(v))).size !== value.length) return "Entries must be different.";
    for (const entry of value) {
      const problem = itemProblem(field, entry);
      if (problem) return problem;
    }
    return null;
  }
  return checkScalar(value, field);
}

/** Inline errors for a document (a whole document or one section's object), by dotted path. */
export function validate(document: Json | undefined, fields: readonly FieldDescriptor[] = FIELDS): SettingsErrors {
  const errors: SettingsErrors = {};
  for (const field of fields) {
    if (isGroup(field)) continue;
    const value = getPath(document, field.path);
    if (value === undefined) continue;
    const problem = checkField(field, value);
    if (problem) errors[field.path] = problem;
  }
  const webSearch = getPath(document, "web_search");
  if (isObject(webSearch) && webSearch["provider"] != null && webSearch["secret_name"] == null) {
    errors["web_search.secret_name"] = "Required when a provider is set.";
  }
  const guardrails = getPath(document, "guardrails");
  if (isObject(guardrails)) {
    if (guardrails["mode"] === "existing" && guardrails["existing"] == null) errors["guardrails.existing"] = "Required when mode is existing.";
    if (guardrails["mode"] === "create" && guardrails["enabled"] === true && guardrails["definition"] == null) {
      errors["guardrails.definition"] = "Required when guardrails are enabled.";
    }
  }
  return errors;
}
